import React, { useState, useCallback, useMemo } from 'react';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { DOC_TYPE } from '../../constants/Constants';
import { CLAIM_VIEWER_SYNC_TYPE } from '../../hooks/useClaimViewerSync';
import ClaimPdfViewer from './claimViewer/ClaimPdfViewer';
import ClaimVideoViewer from './claimViewer/ClaimVideoViewer';
import { useClaimPhotoSwipe } from './claimViewer/useClaimPhotoSwipe';

const url = process.env.REACT_APP_S3_BASE_URL;

const VIEWER_DOC_OPTIONS = DOC_TYPE.filter((d) => d.value !== 'C_DELETE').map(
  (d) => ({ value: d.value, label: d.label })
);

const isPdf = (item) => {
  const name =
    item?.originalFileName ||
    item?.file?.name ||
    (item?.filePath && item.filePath.split('/').pop()) ||
    '';
  return String(name).toLowerCase().endsWith('.pdf');
};

const isVideo = (item) => {
  const name =
    item?.originalFileName ||
    item?.file?.name ||
    (item?.filePath && item.filePath.split('/').pop()) ||
    '';
  const lower = String(name).toLowerCase();
  const mimeType = item?.file?.type || '';
  return (
    mimeType.startsWith('video/') ||
    lower.endsWith('.mp4') ||
    lower.endsWith('.mov') ||
    lower.endsWith('.avi') ||
    lower.endsWith('.mkv') ||
    lower.endsWith('.webm')
  );
};

function itemsMatch(a, b) {
  if (a?.imgId != null && b?.imgId != null) {
    return Number(a.imgId) === Number(b.imgId);
  }
  return a === b;
}

function postViewerSync(images, removed = [], viewerKeys = null) {
  try {
    sessionStorage.setItem('images', JSON.stringify(images));
  } catch (_) {
    /* ignore */
  }
  if (window.opener && !window.opener.closed) {
    window.opener.postMessage(
      {
        type: CLAIM_VIEWER_SYNC_TYPE,
        images,
        removed,
        viewerKeys,
      },
      window.location.origin
    );
  }
}

const ClaimViewerToolbar = ({
  item,
  docOptions,
  onChangeDocumentCode,
  onToggleComplete,
  onDelete,
}) => {
  const docLocked =
    !!item?.imgId && String(item?.docCompleteYn || '').toUpperCase() === 'Y';
  const showToolbar =
    item && typeof item.documentCode === 'string' && docOptions?.length > 0;
  if (!showToolbar) return null;
  return (
    <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-3 mt-1 px-1 border-bottom-1 surface-border pb-2">
      <div className="flex align-items-center gap-2 flex-wrap flex-1">
        {item.imgId ? (
          <label className="flex align-items-center gap-1 m-0 cursor-pointer text-sm">
            <Checkbox
              inputId={`viewer-complete-${item.imgId}`}
              checked={String(item.docCompleteYn || '').toUpperCase() === 'Y'}
              onChange={(e) => onToggleComplete(!!e.checked)}
            />
            <span>완비</span>
          </label>
        ) : null}
        <select
          style={{
            minWidth: '140px',
            maxWidth: 'min(100%, 240px)',
            height: '32px',
            padding: '0 8px',
            fontSize: '12px',
          }}
          value={item.documentCode}
          disabled={docLocked}
          onChange={(e) => onChangeDocumentCode(e.target.value)}
        >
          {docOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <Button
        type="button"
        label="삭제"
        icon="pi pi-trash"
        className="p-button-sm p-button-danger p-button-outlined"
        disabled={docLocked}
        onClick={onDelete}
        title={
          docLocked
            ? '완비된 서류는 삭제할 수 없습니다.'
            : !item.imgId
            ? '저장 전 로컬 파일은 목록에서 제거됩니다.'
            : ''
        }
      />
    </div>
  );
};

function thumbKey(item, idx) {
  if (item?.imgId != null) return `img-${item.imgId}`;
  return `local-${idx}-${item?.documentCode || ''}-${item?.file?.name || ''}`;
}

const ImagesView = () => {
  const initialImages = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem('images')) || [];
    } catch {
      return [];
    }
  }, []);

  const viewerKeys = useMemo(
    () => ({
      imgIds: initialImages
        .filter((i) => i.imgId != null)
        .map((i) => Number(i.imgId)),
      noIdKeys: initialImages
        .filter((i) => i.imgId == null)
        .map((i) => `${i.documentCode}|${i.file?.name || ''}`),
    }),
    [initialImages]
  );

  const [items, setItems] = useState(initialImages);
  const [activeIndex, setActiveIndex] = useState(0);
  const { openGallery } = useClaimPhotoSwipe();

  const getFileUrl = useCallback((item) => {
    if (item?.file?.preview) return item.file.preview;
    const base = process.env.REACT_APP_FILE_SERVER_URL ?? url;
    const path = (item?.filePath || '').trim();
    if (!path)
      return base &&
        (base.startsWith('http://') || base.startsWith('https://'))
        ? base
        : '';
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    const baseNorm = String(base).replace(/\/$/, '');
    const pathPart = path.startsWith('/') ? path : `/${path}`;
    return `${baseNorm}${pathPart}`;
  }, []);

  const updateItem = useCallback(
    (item, patch) => {
      setItems((prev) => {
        const next = prev.map((it) =>
          itemsMatch(it, item) ? { ...it, ...patch } : it
        );
        postViewerSync(next, [], viewerKeys);
        return next;
      });
    },
    [viewerKeys]
  );

  const removeItem = useCallback(
    (item) => {
      setItems((prev) => {
        const idx = prev.findIndex((it) => itemsMatch(it, item));
        if (idx < 0) return prev;
        const next = prev.filter((_, i) => i !== idx);
        const removed =
          item.imgId != null
            ? [
                {
                  imgId: item.imgId,
                  documentCode: item.documentCode,
                  state: 'D',
                },
              ]
            : [];
        postViewerSync(next, removed, viewerKeys);
        setActiveIndex((ai) => {
          if (next.length === 0) return 0;
          if (idx < ai) return Math.max(0, ai - 1);
          if (idx === ai) return Math.min(ai, Math.max(0, next.length - 1));
          return ai;
        });
        return next;
      });
    },
    [viewerKeys]
  );

  const activeItem = items[activeIndex];

  const imageSlides = useMemo(() => {
    return items
      .filter((it) => !isPdf(it) && !isVideo(it))
      .map((it) => ({
        src: getFileUrl(it),
        alt: it.originalFileName || it.file?.name || '',
      }));
  }, [items, getFileUrl]);

  const photoSwipeIndexForActive = useMemo(() => {
    const it = items[activeIndex];
    if (!it || isPdf(it) || isVideo(it)) return -1;
    let ps = 0;
    for (let i = 0; i < activeIndex; i++) {
      if (!isPdf(items[i]) && !isVideo(items[i])) ps++;
    }
    return ps;
  }, [items, activeIndex]);

  const openPhotoSwipe = useCallback(() => {
    if (photoSwipeIndexForActive < 0 || !imageSlides.length) return;
    openGallery(imageSlides, photoSwipeIndexForActive);
  }, [imageSlides, photoSwipeIndexForActive, openGallery]);

  const renderToolbar = (item) => (
    <ClaimViewerToolbar
      item={item}
      docOptions={VIEWER_DOC_OPTIONS}
      onChangeDocumentCode={(code) =>
        updateItem(item, {
          documentCode: code,
          state: item.imgId ? 'U' : item.state,
        })
      }
      onToggleComplete={(checked) =>
        updateItem(item, { docCompleteYn: checked ? 'Y' : 'N' })
      }
      onDelete={() => removeItem(item)}
    />
  );

  const renderThumbInner = (item) => {
    if (isPdf(item)) {
      return (
        <div
          className="flex align-items-center justify-content-center border-1 border-200 border-round"
          style={{ height: 56, width: 56, background: '#f4f4f4' }}
        >
          <i
            className="pi pi-file-pdf"
            style={{ fontSize: '1.5rem', color: '#d32f2f' }}
          />
        </div>
      );
    }
    if (isVideo(item)) {
      return (
        <div
          className="flex align-items-center justify-content-center border-1 border-200 border-round"
          style={{ height: 56, width: 56, background: '#f4f4f4' }}
        >
          <i
            className="pi pi-video"
            style={{ fontSize: '1.5rem', color: '#1976d2' }}
          />
        </div>
      );
    }
    return (
      <img
        src={getFileUrl(item)}
        alt={item.file ? item.file.name : item.originalFileName}
        style={{ display: 'block', height: 56, width: 56, objectFit: 'cover' }}
      />
    );
  };

  if (!items?.length) {
    return (
      <div className="pcr_wrapper p-4">
        <p>보여줄 파일이 없습니다.</p>
      </div>
    );
  }

  const fileName = activeItem?.file
    ? activeItem.file.name
    : activeItem?.originalFileName;

  const toolbar = activeItem ? renderToolbar(activeItem) : null;

  return (
    <div className="pcr_wrapper p-2">
      <div className="flex justify-content-end mb-2">
        <Button
          type="button"
          label="닫기"
          icon="pi pi-times"
          className="p-button-sm p-button-secondary p-button-outlined"
          onClick={() => window.close()}
        />
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2 mb-2 border-bottom-1 surface-border">
        {items.map((item, idx) => (
          <button
            key={thumbKey(item, idx)}
            type="button"
            className={
              idx === activeIndex
                ? 'p-0 border-2 border-primary border-round cursor-pointer bg-transparent'
                : 'p-0 border-1 border-200 border-round cursor-pointer bg-transparent'
            }
            style={{ flex: '0 0 auto' }}
            onClick={() => setActiveIndex(idx)}
          >
            {renderThumbInner(item)}
          </button>
        ))}
      </div>

      {activeItem && isVideo(activeItem) ? (
        <ClaimVideoViewer
          videoSrc={getFileUrl(activeItem)}
          fileName={fileName}
          regTime={activeItem.regTime}
          toolbar={toolbar}
        />
      ) : activeItem && isPdf(activeItem) ? (
        <ClaimPdfViewer
          pdfSrc={getFileUrl(activeItem)}
          fileName={fileName}
          regTime={activeItem.regTime}
          toolbar={toolbar}
        />
      ) : activeItem ? (
        <div>
          {toolbar}
          <ul className="font-bold my-3">
            <li>파일명 : {fileName}</li>
            <li>등록일시 : {activeItem.regTime?.replace('T', ' ')}</li>
          </ul>
          <div className="flex flex-column align-items-center gap-2">
            <img
              src={getFileUrl(activeItem)}
              alt={fileName}
              style={{
                maxHeight: '72vh',
                maxWidth: '100%',
                objectFit: 'contain',
                cursor: 'zoom-in',
              }}
              onClick={openPhotoSwipe}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ImagesView;
